import { hitTestAll } from '@chartv2/engine/hit-test';
import { clsx } from 'clsx';
import React, { useMemo, useState } from 'react';

import styles from '../ChartV2.module.scss';
import type { ChartCanvasContextMenuEvent } from '../engine/ChartCanvas';
import { ChartCanvas } from '../engine/ChartCanvas';
import type {
  ChartV2Handle,
  ChartV2Insight,
  ChartV2LegendEntry,
  ChartV2Locales,
  ChartV2PriceDisplay,
  ChartV2ResolvedSeries,
  RenderLegend,
} from '../types/chart-v2.types';
import type { ContextMenuItem } from '../ui/ContextMenu';
import { ContextMenu } from '../ui/ContextMenu';
import { Legend, PointHover } from '../ui/Legend';
import { PaneActions } from '../ui/PaneActions';
import { PriceDisplayToggle } from '../ui/PriceDisplayToggle';
import type { Pane } from '../utils/panes';
import { InsightsOverlay } from './InsightsOverlay';

/** Horizontal inset (px) between the pane-actions overlay and the axis gutter. */
const PANE_ACTIONS_OVERLAY_INSET = 12;

interface PaneLegendOverlayProps {
  paneId: string;
  paneEntries: ChartV2LegendEntry[];
  allLegendEntries: ChartV2LegendEntry[];
  renderLegend?: RenderLegend;
  showDefaultLegend: boolean;
  isPrimary: boolean;
  locales?: ChartV2Locales;
  pointHover: PointHover | null;
  onSeriesToggle: (seriesId: string, isCurrentlyHidden: boolean) => void;
  onRemoveSeries?: (seriesId: string) => void;
}

const PaneLegendOverlay = ({
  paneId,
  paneEntries,
  allLegendEntries,
  renderLegend,
  showDefaultLegend,
  isPrimary,
  locales,
  onSeriesToggle,
  onRemoveSeries,
  pointHover,
}: PaneLegendOverlayProps) => {
  if (isPrimary && renderLegend) {
    return (
      <div
        className={styles.legendOverlay}
        data-testid={`chartv2-legend-overlay-${paneId}`}
      >
        {renderLegend({
          entries: allLegendEntries,
          handlers: {
            onToggle: onSeriesToggle,
            onRemove: onRemoveSeries,
          },
          pointHover,
        })}
      </div>
    );
  }

  if (!showDefaultLegend || paneEntries.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.legendOverlay}
      data-testid={`chartv2-legend-overlay-${paneId}`}
    >
      <Legend
        entries={paneEntries}
        onToggle={onSeriesToggle}
        onRemove={onRemoveSeries}
        locales={{
          empty: locales?.noData,
          more: locales?.legendMore,
          hide: locales?.legendHide,
          show: locales?.legendShow,
          remove: locales?.legendRemove,
        }}
        pointHover={pointHover}
      />
    </div>
  );
};

export interface PaneViewProps {
  pane: Pane<ChartV2ResolvedSeries>;
  index: number;
  paneCount: number;
  fraction: number;
  isCollapsed: boolean;
  isMaximized: boolean;
  isXAxisPane: boolean;
  paneEntries: ChartV2LegendEntry[];
  allLegendEntries: ChartV2LegendEntry[];
  renderLegend?: RenderLegend;
  showDefaultLegend: boolean;
  locales?: ChartV2Locales;

  sharedDomain: [number, number];
  panExtent: { start: number; end: number };
  showLatestPrice: boolean;
  insights?: ChartV2Insight[];

  canvasRef?: React.Ref<ChartV2Handle>;
  priceDisplay: ChartV2PriceDisplay;
  onPriceDisplayChange: (next: ChartV2PriceDisplay) => void;
  onResetChart: () => void;
  /**
   * Shared column count across all panes so plot widths match and the
   * crosshair / x-axis stay aligned when panes have different series
   * counts.
   */
  axisColumnCount: number;
  /**
   * Shared column width across all panes, sized to fit the widest tick
   * label so long values aren't clipped (kept equal across panes so plot
   * widths stay aligned).
   */
  axisColumnWidth: number;
  sharedHoverTime: number | null;
  onHoverTimeChange: (time: number | null) => void;
  onDomainChange: (range: { start: number; end: number }) => void;
  onSeriesToggle: (seriesId: string, isCurrentlyHidden: boolean) => void;
  onRemoveSeries?: (seriesId: string) => void;

  onPaneMoveUp: () => void;
  onPaneMoveDown: () => void;
  onPaneCollapse?: () => void;
  onPaneMaximize?: () => void;
  onPaneRemove: () => void;
}

export const PaneView = ({
  pane,
  index,
  paneCount,
  fraction,
  isCollapsed,
  isMaximized,
  isXAxisPane,
  paneEntries,
  allLegendEntries,
  renderLegend,
  showDefaultLegend,
  locales,
  sharedDomain,
  panExtent,
  showLatestPrice,
  insights,
  canvasRef,
  priceDisplay,
  onPriceDisplayChange,
  onResetChart,
  axisColumnCount,
  axisColumnWidth,
  onDomainChange,
  onSeriesToggle,
  onRemoveSeries,
  onPaneMoveUp,
  onPaneMoveDown,
  onPaneCollapse,
  onPaneMaximize,
  onPaneRemove,
  sharedHoverTime,
  onHoverTimeChange,
}: PaneViewProps) => {
  const isPrimary = index === 0;
  const isFirst = index === 0;
  const isLast = index === paneCount - 1;
  const flexStyle: React.CSSProperties = isMaximized
    ? { flex: '1 1 0' }
    : isCollapsed
      ? { flex: '0 0 auto' }
      : { flex: `${fraction} 1 0` };
  const controlledDomain = {
    start: sharedDomain[0],
    end: sharedDomain[1],
  };
  const canToggleDisplay = pane.series.length > 1;
  // Pane is "live" once any of its series has at least one datapoint.
  // Drives whether we surface hover affordances (pane actions, the
  // price-display toggle) — they would target a chart with nothing in
  // it otherwise.
  const paneHasContent = pane.series.some(s => s.data.length > 0);
  // Position the pane-actions overlay relative to the reserved axis
  // column gutter (same width across every pane so the icons line up).
  const reservedAxisColumns = Math.max(axisColumnCount, 1);

  const [contextMenu, setContextMenu] =
    useState<ChartCanvasContextMenuEvent | null>(null);

  const contextMenuItems: ContextMenuItem[] = [];

  if (contextMenu && canToggleDisplay && contextMenu.isWithinYAxis) {
    const nextDisplay: ChartV2PriceDisplay =
      priceDisplay === 'indexed' ? 'price' : 'indexed';
    const switchLabel =
      priceDisplay === 'indexed'
        ? (locales?.contextMenuDisplaySplitYAxis ??
          'Display on multiple Y-axis')
        : (locales?.contextMenuDisplayMergedYAxis ??
          'Display on a single Y-axis');

    contextMenuItems.push({
      id: 'toggle-y-axis',
      label: switchLabel,
      onClick: () => onPriceDisplayChange(nextDisplay),
    });
  }

  if (contextMenu) {
    contextMenuItems.push({
      id: 'reset-chart',
      label: locales?.contextMenuResetChart ?? 'Reset chart',
      onClick: onResetChart,
    });
  }

  const pointHover = useMemo<PointHover | null>(() => {
    if (sharedHoverTime == null) {
      return null;
    }

    const hits = hitTestAll(pane.series, sharedHoverTime);
    const snappedTime = hits[0]?.point.time ?? sharedHoverTime;

    return { time: snappedTime, hits };
  }, [sharedHoverTime, pane.series]);

  return (
    <div
      className={clsx(
        styles.pane,
        isPrimary && styles.paneMain,
        isCollapsed && styles.paneCollapsed,
        isMaximized && styles.paneMaximized
      )}
      data-testid={`chartv2-pane-${pane.id}`}
      style={flexStyle}
    >
      <PaneLegendOverlay
        paneId={pane.id}
        paneEntries={paneEntries}
        allLegendEntries={allLegendEntries}
        renderLegend={renderLegend}
        showDefaultLegend={showDefaultLegend}
        isPrimary={isPrimary}
        locales={locales}
        onSeriesToggle={onSeriesToggle}
        onRemoveSeries={onRemoveSeries}
        pointHover={pointHover}
      />
      {paneHasContent && (
        <div
          className={styles.paneActionsOverlay}
          data-testid={`chartv2-pane-actions-overlay-${pane.id}`}
          style={{
            right:
              reservedAxisColumns * axisColumnWidth +
              PANE_ACTIONS_OVERLAY_INSET,
          }}
        >
          <PaneActions
            paneId={pane.id}
            isFirst={isFirst}
            isLast={isLast}
            isPrimary={isPrimary}
            isCollapsed={isCollapsed}
            isMaximized={isMaximized}
            canRemove
            onMoveUp={onPaneMoveUp}
            onMoveDown={onPaneMoveDown}
            onCollapse={onPaneCollapse}
            onMaximize={onPaneMaximize}
            onRemove={onPaneRemove}
          />
          {canToggleDisplay && (
            <PriceDisplayToggle
              value={priceDisplay}
              onChange={onPriceDisplayChange}
              locales={{
                price: locales?.priceDisplayPrice,
                indexed: locales?.priceDisplayIndexed,
              }}
              data-testid={`chartv2-price-display-toggle-${pane.id}`}
            />
          )}
        </div>
      )}
      {!isCollapsed && insights && insights.length > 0 && (
        <InsightsOverlay
          insights={insights}
          domain={sharedDomain}
          seriesCount={pane.series.length}
          axisColumnWidth={axisColumnWidth}
          isXAxisPane={isXAxisPane}
          zoomInToViewText={locales?.zoomInToView}
        />
      )}
      {!isCollapsed && (
        <ChartCanvas
          ref={isPrimary ? canvasRef : undefined}
          series={pane.series}
          axisColumnCount={axisColumnCount}
          axisColumnWidth={axisColumnWidth}
          initialDomain={controlledDomain}
          dataExtent={panExtent}
          locales={{ noData: locales?.noData }}
          onDomainChange={onDomainChange}
          showXAxis={isXAxisPane}
          hoverTime={sharedHoverTime}
          onHoverTimeChange={onHoverTimeChange}
          showLatestPrice={showLatestPrice}
          priceDisplay={priceDisplay}
          onContextMenu={setContextMenu}
        />
      )}
      {contextMenu && contextMenuItems.length > 0 && (
        <ContextMenu
          clientX={contextMenu.clientX}
          clientY={contextMenu.clientY}
          items={contextMenuItems}
          onClose={() => setContextMenu(null)}
          data-testid={`chartv2-context-menu-${pane.id}`}
        />
      )}
    </div>
  );
};
